// Following one session's transcript while its page is open: the items
// already said, then each new one, live.
//
// - It reads the transcript's JSON pages from the last item it holds until
//   it is caught up, then opens the socket from that item (api/transcript.ts).
//   While the socket is not open it polls the JSON page; while it is, it does
//   not. When the socket closes, it polls again and opens another from its
//   last item after a backoff that grows while it keeps failing, as the
//   store's feed does. Without a socket, it polls.
// - Newest first (Poulpe-843): with `newestFirst`, its first catch-up reads
//   the transcript's newest page (`before=end`), draws it and is ready, so
//   the socket opens from that page's last item and nothing said meanwhile
//   is missed. The older pages fill in behind it, one at a time, back to what
//   it already held or to the start, and the whole is folded again in id
//   order after each. Until that gap is filled, nothing is kept on the
//   device, so a kept transcript never has a hole under its mark. A server
//   that answers without `older` read forward and ignored `before` (one
//   before Poulpe-843): it is then read forward, as below.
// - Whatever reaches it twice (a page and the socket can both bring an item)
//   is kept once: items are known by their id.
// - Each read and each socket names the member the pages act as (`actingAs`),
//   as the store's do: the server refuses them, 401 `identity_changed`, once
//   the session's cookie is someone else's.
// - A socket closed with 4401, or a read answered 401, has the store read who
//   is signed in (`onUnauthorized`), and nothing is read or opened meanwhile.
//   When that read leaves the same person signed in, it follows again from
//   its last item: it polls, and opens a socket after a backoff. Otherwise
//   it stops, and the page goes, since the session is not on the board of
//   whoever, if anyone, is signed in now.
// - Once the session has ended, the server sends the items left on the
//   socket and closes it with 1000: the transcript is whole, and nothing more
//   is read or opened. When the store says it ended while no socket is open,
//   one read that catches up does the same; one that ended before the page
//   opened is read, not followed. One a person resumes is followed again,
//   from its last item (`resume`). When an ended session's proposal ends,
//   Poulpe's notice comes after the end: it is read once (`proposalEnded`).
//   Until a read begun after it answers, it stays due: a socket open then
//   may have taken its last page before the notice, so its close reads too.
//   While it is due and no socket is open, it polls: a read that fails on
//   the network or with a 5xx is tried again (Poulpe-697). A 4xx answer
//   settles it as a read would, since asking again would not change it.
// - `stop` closes the socket and drops whatever answers after.
// - What it holds, the page draws: the ACP adapter folds each item in as it
//   comes (transcript/acp.ts), and what the transcript says beside its items
//   (the agent's title, its commands, the first prompt) is read as it comes
//   too (info.ts).
// - Offline, in the Mac app, a session of this Mac's runner shows what the
//   runner holds after what the device kept (`local`, offline design §3,
//   Poulpe-828), under "From this Mac's runner". The runner's items have its
//   own ids, not the server's: the overlay is never kept on the device and
//   never moves the cursor. What the runner and the device both hold is
//   dropped by sequence: the longest run at the end of what the device kept
//   that equals, payload by payload, a run at the start of the runner's
//   items. Once the server answers again, a read or the socket, the overlay
//   goes, and the transcript reads on from its own mark.
import { ApiError, errorMessage } from '../api/client'
import { itemOf, TRANSCRIPT_ABNORMAL, TRANSCRIPT_ENDED, TRANSCRIPT_UNAUTHORIZED, type OpenTranscript, type TranscriptSocket } from '../api/transcript'
import type { LocalPage } from '../api/shellLocalReads'
import type { Api, TranscriptItem, TranscriptPage } from '../api/types'
import { FEED_STABLE_MS, reconnectBackoff } from '../store/store'
import type { TranscriptCache } from '../store/local'
import { EMPTY_FOLD, foldAcp, type AcpFold, type EngineAgent } from '../transcript/acp'
import type { Item } from '../transcript/types'
import { infoOf, NO_INFO, type SessionInfo } from './info'

export interface TranscriptView {
  subagents?: readonly EngineAgent[]
  /** Kept items remain visible when the server cannot be reached. */
  offline?: boolean
  /** Offline, what this Mac's runner holds is drawn after what the device kept (Poulpe-828). */
  local?: boolean
  /** `ready` once it has caught up, by a read or an open socket. */
  status: 'loading' | 'ready' | 'error'
  /** Why it could not read the transcript yet; null once it has. */
  error: ApiError | null
  /** What the page draws, from the items read so far. */
  items: readonly Item[]
  /** The session's folder, once a call shows it. */
  cwd: string | null
  /** The socket is open and brings the items. */
  live: boolean
  /**
   * A complete server read has brought it up to date, as saved items alone never do. An open socket
   * does not say so: it opens before it brings what was missed.
   */
  caughtUp?: boolean
  /** What the transcript says beside its items: the agent's title, its commands, the first prompt. */
  info: SessionInfo
}

export interface TranscriptOptions {
  cache?: TranscriptCache | null
  api: Pick<Api, 'transcript'> & Partial<Pick<Api, 'transcriptBefore'>>
  /** Read the newest page first, and fill the older ones behind it (Poulpe-843); forward from the start otherwise. */
  newestFirst?: boolean
  session: string
  /** The session has ended: its transcript is read, not followed. */
  ended?: boolean
  /** Opens the transcript's socket; none, and it polls. */
  open?: OpenTranscript | null
  /** How often to read the JSON page while no socket brings the items; 0 reads it only at the start. */
  pollMs?: number
  /** How long to wait before opening the socket again, after `attempt` failures in a row (0 for the first). */
  reconnectMs?: (attempt: number) => number
  /** The member the pages act as, which each read and socket names; null until it is known. */
  actingAs?: () => string | null
  /**
   * The socket or a read said that the person's session or token ended, or
   * is someone else's: the store reads who is signed in. True when the same
   * person still is, and the transcript follows again.
   */
  onUnauthorized?: () => Promise<boolean>
  /**
   * Reads a page of what this Mac's runner holds of the session, after its
   * item `after`; null when it gives nothing. Given only inside the Mac app,
   * for a session of this Mac's runner, and read only while offline.
   */
  local?: ((after: string | null) => Promise<LocalPage | null>) | null
}

/** The most payloads of the last items held that the overlay is aligned with: the runner keeps 256 acknowledged events. */
const KEPT_TAIL = 1024
/** The most pages of the runner's items read at once. */
const LOCAL_PAGES = 50

/** A payload as text, its keys sorted: the server's JSON need not keep the runner's order. */
export function canonical(value: unknown): string {
  return JSON.stringify(value, (_key, v: unknown) => typeof v === 'object' && v !== null && !Array.isArray(v)
    ? Object.fromEntries(Object.entries(v as Record<string, unknown>).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)))
    : v)
}

/** How many of `runner`'s first items the end of `kept` already holds: the longest such run, compared in order. */
export function overlap(kept: readonly string[], runner: readonly string[]): number {
  for (let k = Math.min(kept.length, runner.length); k > 0; k--) {
    let same = true
    for (let i = 0; i < k && same; i++) same = kept[kept.length - k + i] === runner[i]
    if (same) return k
  }
  return 0
}

const LOADING: TranscriptView = { status: 'loading', error: null, items: [], cwd: null, live: false, info: NO_INFO }

function asApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError(0, 'unknown', errorMessage(err))
}

export class TranscriptFollower {
  private state: TranscriptView = LOADING
  private readonly listeners = new Set<() => void>()
  private fold: AcpFold = EMPTY_FOLD
  /** Every item held, by id. */
  private readonly held = new Set<string>()
  /** The last item held: the socket and the reads go on after it. */
  private cursor: string | null = null
  private running = false
  /** The store reads who is signed in: nothing is read or opened until it has. */
  private waiting = false
  /** Stopped for good: someone else, or no one, is signed in now. */
  private halted = false
  private ended: boolean
  /** A read since the session ended has caught up: nothing more is read. */
  private settled = false
  /** The proposal endings heard, and those a read begun after them has answered for (Poulpe-696). */
  private proposalEnds = 0
  private proposalEndsRead = 0
  /** Bumped by `stop`, so that no answer from before reaches what follows. */
  private generation = 0
  private reading: Promise<void> | null = null
  private timer: ReturnType<typeof setTimeout> | null = null
  private socket: TranscriptSocket | null = null
  private live = false
  private openedAt = 0
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  /** The sockets that failed in a row. */
  private attempts = 0
  private cache: TranscriptCache | null
  private hydrated = false
  private pendingKept: TranscriptItem[] = []
  private cacheTimer: ReturnType<typeof setTimeout> | null = null
  private releaseCache: (() => void) | null = null
  /** What the transcript says beside the items held, without the overlay. */
  private info: SessionInfo = NO_INFO
  /** The payloads of the last items held, as `canonical` writes them. */
  private keptTail: string[] = []
  private readonly local: ((after: string | null) => Promise<LocalPage | null>) | null
  /** What this Mac's runner holds of the session, read while offline: never kept, never the cursor. */
  private localItems: TranscriptItem[] = []
  private localPayloads: string[] = []
  private readonly localIds = new Set<string>()
  private localCursor: string | null = null
  private localReading = false
  /** Bumped when the overlay goes, so that no runner read from before reaches the view. */
  private localGeneration = 0

  /** Every item held, in id order: what a fold of the whole starts from once older items come in. */
  private all: TranscriptItem[] = []
  /** The first item after the run not read yet, while the older pages fill in behind the newest; null when none is missing. */
  private gap: string | null = null
  /** The items held while a gap is open, kept on the device only once it is filled. */
  private gapKept: TranscriptItem[] = []
  /** The first catch-up has asked for the newest page. */
  private triedNewest = false
  /** The generation whose fill runs; one fill at a time. */
  private fillingIn: number | null = null
  private fillTimer: ReturnType<typeof setTimeout> | null = null
  private fillAttempts = 0

  private readonly api: Pick<Api, 'transcript'> & Partial<Pick<Api, 'transcriptBefore'>>
  private readonly newestFirst: boolean
  private readonly session: string
  private readonly open: OpenTranscript | null
  private readonly pollMs: number
  private readonly reconnectMs: (attempt: number) => number
  private readonly actingAs: () => string | null
  private readonly onUnauthorized: () => Promise<boolean>

  constructor(options: TranscriptOptions) {
    this.cache = options.cache ?? null
    this.api = options.api
    this.newestFirst = options.newestFirst ?? false
    this.session = options.session
    this.ended = options.ended ?? false
    this.open = options.open ?? null
    this.pollMs = options.pollMs ?? 2_000
    this.reconnectMs = options.reconnectMs ?? reconnectBackoff
    this.actingAs = options.actingAs ?? (() => null)
    this.onUnauthorized = options.onUnauthorized ?? (() => Promise.resolve(false))
    this.local = options.local ?? null
  }

  // --- React binding (useSyncExternalStore) ----------------------------------

  getState = (): TranscriptView => this.state

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  // --- lifecycle ----------------------------------------------------------------

  /** Read what is said, then follow it: live when it can, by polling otherwise. It may start again after `stop`. */
  start(): void {
    if (this.running || this.halted) return
    this.running = true
    this.releaseCache = this.cache?.beforeClose?.(() => {
      this.stop()
      this.cache = null
      this.halted = true
    }) ?? null
    if (!this.running) return
    const generation = this.generation
    const begin = () => {
      if (generation !== this.generation || !this.running) return Promise.resolve()
      this.schedule()
      return this.catchUp()
    }
    const reading = this.cache ? this.restore(generation).then(begin) : begin()
    void reading.then(() => {
      if (generation === this.generation) this.follow()
    })
  }

  private async restore(generation: number): Promise<void> {
    if (this.hydrated || !this.cache) return
    try {
      const kept = await this.cache.readTranscript(this.session)
      if (generation !== this.generation) return
      this.hydrated = true
      if (!kept) return
      this.take(kept.items, false)
      this.cursor = kept.after
      if (kept.items.length) this.setState({ status: 'ready' })
    } catch {
      if (generation === this.generation) this.cache = null
    }
  }

  private keep(items: readonly TranscriptItem[]): void {
    if (!this.cache) return
    if (this.gap !== null) {
      this.gapKept.push(...items)
      return
    }
    this.pendingKept.push(...items)
    if (this.cacheTimer === null) this.cacheTimer = setTimeout(() => this.flushCache(), 1_000)
  }

  /** One transaction for each second of streamed items; lifecycle boundaries flush immediately. */
  private flushCache(): void {
    if (this.cacheTimer !== null) clearTimeout(this.cacheTimer)
    this.cacheTimer = null
    const cache = this.cache
    if (!cache || !this.pendingKept.length) return
    const items = this.pendingKept
    this.pendingKept = []
    void cache.appendTranscript(this.session, items, this.cursor).catch(() => {
      if (this.cache !== cache) return
      this.cache = null
      if (this.cacheTimer !== null) clearTimeout(this.cacheTimer)
      this.cacheTimer = null
      this.pendingKept = []
    })
  }

  /** Whether `start` can follow it again: not once its cache closed or its person changed. */
  canStart(): boolean {
    return !this.halted
  }

  /** Close the socket and stop reading; nothing that answers after reaches the view. */
  stop(): void {
    this.running = false
    this.waiting = false
    this.generation++
    this.reading = null
    this.closeAll()
    this.releaseCache?.()
    this.releaseCache = null
  }

  /** The session has ended: no socket opens again, and the reads stop once one has caught up. */
  end(): void {
    this.flushCache()
    if (this.ended) return
    this.ended = true
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer)
    this.reconnectTimer = null
    // What was said last may not have reached a socket that is down.
    if (this.running && !this.waiting && !this.live && (!this.cache || this.hydrated)) void this.catchUp().finally(() => this.schedule())
  }

  /**
   * A proposal of the ended session ended (Poulpe-565): Poulpe wrote its
   * notice after the session's end, so a transcript read whole is read again,
   * once. A read on its way may have been answered before the notice was
   * written, so a fresh one follows it. An open socket may have taken its last
   * page before the notice too (Poulpe-696): it stays due until a read begun
   * after it answers, and the socket's close reads it.
   */
  proposalEnded(): void {
    if (!this.ended) return
    this.proposalEnds++
    this.readProposal()
  }

  /** Read once for a proposal ending no read has answered for yet, after the read on its way, unless a socket is open. */
  private readProposal(): void {
    const again = () => {
      if (this.proposalEndsRead < this.proposalEnds && this.ended && this.running && !this.waiting && !this.live) void this.catchUp().finally(() => this.schedule())
    }
    const before = this.reading
    if (before) void before.then(again, again)
    else again()
  }

  /** The session runs again, resumed (Poulpe-425): it is followed again from the last item held, live when it can. */
  resume(): void {
    if (!this.ended) return
    this.ended = false
    this.settled = false
    this.attempts = 0
    if (!this.running || this.waiting) return
    this.schedule()
    this.follow()
  }

  private closeAll(): void {
    this.flushCache()
    if (this.timer !== null) clearTimeout(this.timer)
    this.timer = null
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer)
    this.reconnectTimer = null
    if (this.fillTimer !== null) clearTimeout(this.fillTimer)
    this.fillTimer = null
    const socket = this.socket
    this.socket = null
    this.live = false
    socket?.close()
    if (this.state.live) this.setState({ live: false })
  }

  /**
   * The person's session or token ended, or is someone else's: read and open
   * nothing until the store has read who is signed in; then follow again from
   * the last item if it is the same person, or stop for good.
   */
  private unauthorized(): void {
    if (this.waiting) return
    this.waiting = true
    this.generation++
    this.reading = null
    this.closeAll()
    const generation = this.generation
    void this.onUnauthorized()
      .catch(() => false)
      .then((same) => {
        if (generation !== this.generation || !this.running) return
        this.waiting = false
        if (!same) {
          this.halted = true
          this.running = false
          return
        }
        this.again()
      })
  }

  /** Follow again after a socket closed, or a read was refused: poll, and open another socket after a backoff; once ended, read what is left. */
  private again(): void {
    if (this.ended) {
      void this.catchUp().finally(() => this.schedule())
      return
    }
    this.schedule()
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer)
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.follow()
    }, this.reconnectMs(this.attempts++))
  }

  // --- reading the JSON pages ------------------------------------------------------

  /** The session has ended, a read since has caught up, and no proposal ending is still due: nothing more is read. */
  private whole(): boolean {
    return this.ended && this.settled && this.proposalEndsRead >= this.proposalEnds
  }

  /** Poll, unless a socket brings the items, or the transcript is whole. */
  private schedule(): void {
    if (!this.running || this.waiting || this.pollMs <= 0 || this.timer !== null || this.live || this.whole()) return
    this.timer = setTimeout(() => {
      this.timer = null
      if (!this.running || this.live || this.whole()) return
      void this.catchUp().finally(() => this.schedule())
    }, this.pollMs)
  }

  /** Read the pages after the last item held until caught up. One read at a time: a second caller shares it. */
  catchUp(): Promise<void> {
    if (this.reading) return this.reading
    const reading: Promise<void> = this.read().finally(() => {
      if (this.reading === reading) this.reading = null
    })
    this.reading = reading
    return reading
  }

  private async read(): Promise<void> {
    const generation = this.generation
    const afterEnd = this.ended
    const proposalEnds = this.proposalEnds
    // What the pages bring is drawn once, when the reading stops: drawn page
    // by page, a long transcript replayed itself each time it was opened.
    const read: unknown[] = []
    let cursor = this.cursor
    const draw = () => {
      this.take(read)
      if (cursor !== null) this.cursor = cursor
    }
    const failed = (err: unknown) => {
      draw()
      if (err instanceof ApiError && err.status === 401) {
        this.unauthorized()
        return
      }
      // A transcript shown stays as it is: the next poll tries again.
      if (!(err instanceof ApiError) || err.status === 0 || err.status >= 500) {
        this.setState({ offline: this.cache !== null })
        void this.readLocal()
      }
      // Asking again would get the same 4xx: the proposal endings it was read for are not retried (Poulpe-697).
      else if (err.status >= 400 && err.status < 500) this.proposalEndsRead = Math.max(this.proposalEndsRead, proposalEnds)
      if (this.state.status !== 'ready') this.setState({ status: 'error', error: asApiError(err) })
    }
    let newest = false
    if (this.newestFirst && !this.triedNewest && this.api.transcriptBefore) {
      try {
        newest = await this.readNewest(generation)
      } catch (err) {
        // Asked for again by the next read.
        if (generation === this.generation) failed(err)
        return
      }
    }
    if (generation !== this.generation) return
    // Taken already, the newest page moved the cursor: the forward read and its drawing are skipped.
    if (!newest) for (;;) {
      const from = cursor
      let page: TranscriptPage
      try {
        page = await this.api.transcript(this.session, from, this.actingAs())
      } catch (err) {
        if (generation === this.generation) failed(err)
        return
      }
      if (generation !== this.generation) return
      const items = Array.isArray(page?.items) ? page.items : []
      read.push(...items)
      // Past an item that is not one, too, so that it is not read again and again.
      const last: unknown = items.at(-1)
      const lastId = typeof last === 'object' && last !== null ? (last as { id?: unknown }).id : undefined
      if (typeof lastId === 'string' && lastId !== '') cursor = lastId
      if (page.more !== true || cursor === from) break
    }
    if (!newest) draw()
    this.proposalEndsRead = Math.max(this.proposalEndsRead, proposalEnds)
    if (afterEnd) { this.settled = true; this.flushCache() }
    this.dropLocal()
    if (this.state.status !== 'ready' || this.state.offline || !this.state.caughtUp) this.setState({ status: 'ready', error: null, offline: false, caughtUp: true })
    if (this.gap !== null) void this.fill()
  }

  /**
   * The newest page, taken after what is held: true once taken. False when
   * the server read forward instead (it answered without `older`): the
   * forward read follows. A failed read throws, and is asked for again.
   */
  private async readNewest(generation: number): Promise<boolean> {
    this.triedNewest = true
    let page: TranscriptPage
    try {
      page = await this.api.transcriptBefore!(this.session, 'end', this.actingAs())
    } catch (err) {
      if (generation === this.generation) this.triedNewest = false
      throw err
    }
    if (generation !== this.generation) return true
    if (typeof page?.older !== 'boolean') return false
    const items = (Array.isArray(page.items) ? page.items : []).map(itemOf).filter((item): item is TranscriptItem => item !== null)
    const first = items[0]
    // Older items precede it that are not held: they fill in behind it.
    if (first && page.older && !this.held.has(first.id) && (this.cursor === null || first.id > this.cursor)) this.gap = first.id
    this.take(items)
    return true
  }

  /** Read the pages before the gap, newest first, until it reaches what is held or the start; folded again after each. */
  private async fill(): Promise<void> {
    const before = this.api.transcriptBefore
    if (!before || this.gap === null || !this.running || this.waiting || this.fillingIn === this.generation) return
    const generation = this.generation
    this.fillingIn = generation
    try {
      while (this.gap !== null) {
        let page: TranscriptPage
        try {
          page = await before(this.session, this.gap, this.actingAs())
        } catch (err) {
          if (generation !== this.generation) return
          if (err instanceof ApiError && err.status === 401) {
            this.unauthorized()
            return
          }
          // Tried again after a backoff, as a socket is; the newest items stay drawn meanwhile.
          this.fillTimer = setTimeout(() => {
            this.fillTimer = null
            void this.fill()
          }, this.reconnectMs(this.fillAttempts++))
          return
        }
        if (generation !== this.generation) return
        this.fillAttempts = 0
        const items = (Array.isArray(page?.items) ? page.items : []).map(itemOf).filter((item): item is TranscriptItem => item !== null)
        const fresh = items.filter((item) => !this.held.has(item.id))
        // It reached what is held, or the start; a page without `older` cannot say, so it stops there too.
        const reached = items.length === 0 || fresh.length < items.length || page.older !== true
        this.prepend(fresh)
        this.gap = reached ? null : items[0]!.id
      }
      this.pendingKept.push(...this.gapKept)
      this.gapKept = []
      this.flushCache()
    } finally {
      if (this.fillingIn === generation) this.fillingIn = null
    }
  }

  /** Hold items older than the newest held, and fold the whole again in id order. */
  private prepend(older: readonly TranscriptItem[]): void {
    if (older.length === 0) return
    for (const item of older) this.held.add(item.id)
    this.all = [...this.all, ...older].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
    this.keep(older)
    this.fold = foldAcp(EMPTY_FOLD, this.all)
    this.info = infoOf(NO_INFO, this.all)
    this.keptTail = this.all.slice(-KEPT_TAIL).map((item) => canonical(item.payload))
    if (this.state.local) this.drawLocal()
    else this.setState({ items: this.fold.items, cwd: this.fold.cwd, info: this.info, subagents: [...this.fold.subagents.values()] })
  }

  /** Keep the items not held yet, in order: held, and past the cursor, once they are in the fold, not before. */
  private take(values: readonly unknown[], persist = true): void {
    const fresh: TranscriptItem[] = []
    const seen = new Set<string>()
    for (const value of values) {
      const item = itemOf(value)
      if (!item || this.held.has(item.id) || seen.has(item.id)) continue
      seen.add(item.id)
      fresh.push(item)
    }
    if (fresh.length === 0) return
    const fold = foldAcp(this.fold, fresh)
    const info = infoOf(this.info, fresh)
    for (const id of seen) this.held.add(id)
    this.all.push(...fresh)
    this.cursor = fresh.at(-1)!.id
    for (const item of fresh) this.keptTail.push(canonical(item.payload))
    if (this.keptTail.length > KEPT_TAIL) this.keptTail.splice(0, this.keptTail.length - KEPT_TAIL)
    if (persist) this.keep(fresh)
    if (fold === this.fold && info === this.info) return
    this.fold = fold
    this.info = info
    if (this.state.local) this.drawLocal()
    else this.setState({ items: fold.items, cwd: fold.cwd, info, subagents: [...fold.subagents.values()] })
  }

  // --- this Mac's runner, offline (Poulpe-828) ----------------------------------------

  /** Read what this Mac's runner holds since its last item read, and draw it after what the device kept. */
  private async readLocal(): Promise<void> {
    const local = this.local
    if (!local || this.localReading || !this.running) return
    this.localReading = true
    const generation = this.localGeneration
    try {
      let cursor = this.localCursor
      const fresh: TranscriptItem[] = []
      for (let i = 0; i < LOCAL_PAGES; i++) {
        let page: LocalPage | null
        try { page = await local(cursor) } catch { page = null }
        if (generation !== this.localGeneration) return
        if (!page) break
        let added = false
        for (const item of page.items) {
          // An `after` the runner no longer holds reads from its oldest again: what is held already is skipped.
          if (this.localIds.has(item.id) || fresh.some(f => f.id === item.id)) continue
          fresh.push(item)
          added = true
        }
        if (page.items.length) cursor = page.items.at(-1)!.id
        if (!page.more || !added) break
      }
      if (!fresh.length) return
      for (const item of fresh) {
        this.localIds.add(item.id)
        this.localItems.push(item)
        this.localPayloads.push(canonical(item.payload))
      }
      this.localCursor = cursor
      this.drawLocal()
    } finally {
      if (generation === this.localGeneration) this.localReading = false
    }
  }

  /** The items held, then the runner's after the run both hold. */
  private drawLocal(): void {
    const overlay = this.localItems.slice(overlap(this.keptTail, this.localPayloads))
    const fold = foldAcp(this.fold, overlay)
    this.setState({
      items: fold.items, cwd: fold.cwd, info: infoOf(this.info, overlay), subagents: [...fold.subagents.values()],
      local: true, ...(this.state.status === 'ready' ? {} : { status: 'ready', error: null }),
    })
  }

  /** The server answers again: the overlay goes, and what the device holds is drawn. */
  private dropLocal(): void {
    this.localGeneration++
    this.localReading = false
    this.localItems = []
    this.localPayloads = []
    this.localIds.clear()
    this.localCursor = null
    if (!this.state.local) return
    const fold = this.fold
    this.setState({ items: fold.items, cwd: fold.cwd, info: this.info, subagents: [...fold.subagents.values()], local: false })
  }

  // --- the socket --------------------------------------------------------------------

  /** Open the socket from the last item held, unless one is open or opening, one is due after a backoff, or the session has ended. */
  private follow(): void {
    if (!this.open || !this.running || this.waiting || this.ended) return
    if (this.socket !== null || this.reconnectTimer !== null) return
    const generation = this.generation
    let socket: TranscriptSocket | null = null
    // What a socket sends counts while it is this one's, since it last started.
    const current = () => socket !== null && socket === this.socket && generation === this.generation
    try {
      socket = this.open(
        this.session,
        this.cursor,
        {
          open: () => {
            if (current()) this.opened()
          },
          item: (item) => {
            if (!current()) return
            this.dropLocal()
            this.take([item])
          },
          closed: (code) => {
            if (current()) this.closed(code)
          },
        },
        this.actingAs(),
      )
    } catch {
      // The browser would not even try: as a socket that failed.
      this.closed(TRANSCRIPT_ABNORMAL)
      return
    }
    this.socket = socket
  }

  private opened(): void {
    this.live = true
    this.openedAt = Date.now()
    if (this.timer !== null) clearTimeout(this.timer)
    this.timer = null
    this.dropLocal()
    this.setState({ live: true, offline: false, ...(this.state.status === 'ready' ? {} : { status: 'ready', error: null }) })
  }

  /**
   * The socket closed: with 1000, the session has ended and the transcript is
   * whole; with 4401, the store reads who is signed in; otherwise poll, and
   * open another after a backoff.
   */
  private closed(code: number): void {
    this.flushCache()
    this.socket = null
    if (this.live && Date.now() - this.openedAt >= FEED_STABLE_MS) this.attempts = 0
    this.live = false
    if (this.state.live) this.setState({ live: false })
    if (!this.running) return
    if (code === TRANSCRIPT_ENDED) {
      // The server sent every item before it closed.
      this.ended = true
      this.settled = true
      if (this.timer !== null) clearTimeout(this.timer)
      this.timer = null
      // Unless a proposal ended since its last page may have been taken.
      this.readProposal()
      return
    }
    if (code === TRANSCRIPT_UNAUTHORIZED) {
      this.unauthorized()
      return
    }
    this.setState({ offline: this.cache !== null })
    void this.readLocal()
    this.again()
  }

  private setState(partial: Partial<TranscriptView>): void {
    this.state = { ...this.state, ...partial }
    for (const listener of this.listeners) listener()
  }
}
